import {
  PostEditorLinkRemoveAction,
  PostEditorLinkUpdateAction,
  PostEditorLinkUpdateMetadataAction,
  PostEditorLinkUpdateSettingsAction,
  PostEditorReducerFunction
} from '../PostEditorActions';
import { ImmutabilityContext } from '../effects/customImmutabilityContext';
import { getUrlParams } from '../../../../helpers/getUrlParams';
import { LinkDestination, UtmInput } from '../../../../models/graphql';
import { cloneDeep } from 'lodash';
import { BaseLinkSettings } from '../../TextEditor/helpers/onChange';
import { EditorText, GroupPostAttributesType } from '../../../../models/post-editor';
import { Element } from 'slate';
import { OnStateUpdateCallbackFunction, postGroupStateUpdater } from '../../helpers/postGroupStateUpdater';
import { validatorStateUpdater } from '../../validators/helpers/validatorStateUpdater';
import { ErrorMessageSeverity } from '../../../../constants/form';

export const UtmInputKeys = ['campaign', 'content', 'medium', 'source', 'term'];

export const IsKeyOfUtmInput = (value: string): value is keyof UtmInput => {
  return UtmInputKeys.includes(value);
};

export const getExistingUtmKeys = (initialUrl: string): string[] => {
  const trimmedUrl = initialUrl.trim();
  const urlized = /^https?:\/\//.test(trimmedUrl) ? trimmedUrl : `http://${trimmedUrl}`;

  const urlObject: URL | null = getUrlParams(urlized);
  if (!urlObject) {
    console.error('initial URL could not be converted to URL object.')
    return [];
  }

  const { searchParams } = urlObject;
  let urlKeys: string[] = [];
  for (const key of Array.from(searchParams.keys())) {
    urlKeys = [...urlKeys, key];
  }
  return urlKeys.filter(k => k.includes('utm_'));
};

export const makeFinalLink = (link: BaseLinkSettings & { initialUrl: string }, returnFinalAsBitly = true): string => {
  const { initialUrl, utmTracking, addUtm, removeUtm, useBitly, destination, linkMetadata } = link;
  // if initial link does not start with http, we should prepened it
  const trimmedUrl = initialUrl.trim();
  const urlized =
    destination === LinkDestination.EXPANDED && linkMetadata
      ? linkMetadata.expandedUrl
      : /^https?:\/\//.test(trimmedUrl)
      ? trimmedUrl
      : `http://${trimmedUrl}`;
  const urlObject: URL | null = getUrlParams(urlized);
  if (!urlObject) {
    console.error('initial URL could not be converted to URL object.');
    return '';
  }
  const { searchParams } = urlObject;
  let urlKeys: string[] = [];
  for (const key of Array.from(searchParams.keys())) {
    urlKeys = [...urlKeys, key];
  }
  const existingUtmKeys = urlKeys.filter(k => k.startsWith('utm_'));
  if (removeUtm && addUtm && utmTracking) {
    for (const k of existingUtmKeys) {
      searchParams.delete(k);
    }
    for (const [key, value] of Object.entries(utmTracking)) {
      if (value) {
        searchParams.set(`utm_${key}`, value);
      }
    }
  } else if (!removeUtm && addUtm && utmTracking) {
    for (const [key, value] of Object.entries(utmTracking)) {
      if (value && !existingUtmKeys.includes(`utm_${key}`)) {
        searchParams.set(`utm_${key}`, value);
      }
    }
  } else if (removeUtm && !addUtm) {
    for (const k of existingUtmKeys) {
      searchParams.delete(k);
    }
  }
  if (useBitly)
    return linkMetadata?.isBitlink && destination !== LinkDestination.EXPANDED
      ? linkMetadata.initialUrl
      : returnFinalAsBitly
      ? `${urlObject.protocol}//bit.ly/placeholder`
      : urlObject.href;
  return urlObject.href;
};

export const removeLinkReducer: PostEditorReducerFunction<PostEditorLinkRemoveAction['payload']> = (state, payload) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { postGroupIndex, customizeIndex, editors } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost =>
    updateImmutable<GroupPostAttributesType>(currentPost, {
      content: {
        link: { $set: null },
        // We'll updating editor texts to reflect the state that we have in editor, since we have removed links from there, and now we're just updating the state to reflect that
        texts: {
          $apply: (current: EditorText[]) =>
            current.map(editorText => {
              const currEditor = editors.current[postGroupIndex].find(e => e.id === editorText.id);
              if (!currEditor) throw new Error('Editor Mapping Failed.');
              const updatedText = currEditor.editor.children.flatMap(elem =>
                Element.isElement(elem) && elem.type === 'paragraph' ? elem : []
              );
              return {
                ...editorText,
                text: updatedText
              };
            })
        }
      }
    });
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, [
    'checkLinksPresent',
    'profileRequired'
  ]);
};
export const updateLinkReducer: PostEditorReducerFunction<PostEditorLinkUpdateAction['payload']> = (state, payload) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { link, postGroupIndex, customizeIndex } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost =>
    updateImmutable(currentPost, {
      content: {
        link: { $set: link }
      }
    });
  return postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
};

export const updateLinkMetadataReducer: PostEditorReducerFunction<PostEditorLinkUpdateMetadataAction['payload']> = (
  state,
  payload
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { linkMetadata, postGroupIndex, customizeIndex } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost =>
    updateImmutable<GroupPostAttributesType>(currentPost, {
      content: {
        link: {
          useBitly: { $apply: current => (linkMetadata?.isBitlink ? false : current) },
          linkMetadata: { $set: linkMetadata },
          url: { $apply: current => (linkMetadata?.isBitlink ? linkMetadata.initialUrl : current) }
        }
      }
    });
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, ['mediaRequired']);
};

const isKeyOfLinkSettingsGuard = (
  value: string
): value is keyof PostEditorLinkUpdateSettingsAction['payload']['settings'] => {
  return [
    'googleCallToAction',
    'destination',
    'useBitly',
    'utmTracking',
    'addUtm',
    'removeUtm',
    'removePreview'
  ].includes(value);
};

export const updateLinkSettingsReducer: PostEditorReducerFunction<PostEditorLinkUpdateSettingsAction['payload']> = (
  state,
  payload
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { postGroupIndex, customizeIndex, settings } = payload;
  let keys: Array<keyof PostEditorLinkUpdateSettingsAction['payload']['settings']> = [];
  for (const key of Object.keys(settings)) {
    if (isKeyOfLinkSettingsGuard(key)) {
      keys = [...keys, key];
    }
  }
  const callback: OnStateUpdateCallbackFunction = currentPost =>
    updateImmutable<GroupPostAttributesType>(currentPost, {
      content: {
        link: current => {
          let updated = updateImmutable(current, {});
          keys.forEach(k => {
            const state = cloneDeep(updated);
            updated = updateImmutable(state, {
              [k]: { $set: settings[k] }
            });
          });
          return updated;
        }
      }
    });
  return postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
};
